import { useStripe } from '@stripe/react-stripe-js';
import { useEffect, useState } from 'react';
import { Card, Col, Form, Modal, Row, Stack } from 'react-bootstrap';
import { ReactComponent as SFCLogoLarge } from '../../../../assets/icons/sfc-logo-large.svg';
import { SimpleButton } from '../../../../components/buttons/SimpleButton';
import { LabelTextSpread } from '../../../../components/generic';
import CurrencyCell from '../../../../components/tables/cellComponents/CurrencyCell';
import { FormSelect, FormSelectOptions } from '../../../../components/tables/filterForms/FormSelect';
import { useLazySearchPromotionalCodeQuery } from '../../../../services/endpoints/orders/promotionalCode';
import {
  Discount,
  LocationOrders,
  OrderPricing,
  PromotionalCode
} from '../../../../types/orders.type';
import { titleCase } from '../../../../utils/stringConversions';
import { getErrorMessage } from '../../../../utils/utils';
import {
  formatDiscountOptionLabel,
  getDiscountedOrderTotal,
  getRegistrationPricingLines
} from '../../../../utils/orders/discountApplication';
import { StepProps } from '../OrderCheckout';
import { OrderCard } from './components/OrderCard';
import { OrderCost } from './components/OrderCost';
import { SearchField } from './components/SearchField';
import PaymentForm from './PaymentForm';
import { useUpdateOrderTransactionMutation } from '../../../../services/endpoints/orders/orders';
import { useNavigate } from 'react-router-dom';

const clientSecret_qs = new URLSearchParams(window.location.search).get(
  'payment_intent_client_secret'
);

const locationId_qs = new URLSearchParams(window.location.search).get('location_id');

export const OrderReview: React.FC<StepProps> = ({ values, next }) => {
  // Promo codes
  const [searchPromoCode] = useLazySearchPromotionalCodeQuery();
  const [promoCodeMessage, setPromoCodeMessage] = useState<{
    msg: string;
    type: 'valid' | 'invalid';
  }>();
  const [promoCode, setPromoCode] = useState<PromotionalCode>();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [appliedDiscounts, setAppliedDiscounts] = useState<Discount[]>([]);
  const [scholarshipError, setScholarshipError] = useState<string>();
  const [locationOrders, setLocationOrders] = useState<LocationOrders>();

  const stripe = useStripe();
  const [updateOrderTransaction] = useUpdateOrderTransactionMutation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!stripe) {
      return;
    }

    if (!clientSecret_qs) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret_qs).then(({ paymentIntent }) => {
      if (!paymentIntent) {
        return;
      }

      if (!locationId_qs) {
        return;
      }

      if (locationId_qs === String(values.location.id)) {
        if (paymentIntent.status === 'succeeded') {
          next();
        } else {
          console.log('Error: Payment status: ', paymentIntent.status);
        }
      }
    });
  }, [stripe]);

  useEffect(() => {
    let locOrders = values.orders?.reduce((acc, curr) => {
      let location = values.location;
      if ((curr.registrations?.length ?? 0) > 0) {
        const promoDiscount = (promoCode?.percent_discount ?? 0) / 100;
        const price = curr.registrations.reduce((acc, r) => acc + r.price, 0);
        const discountedTotal = getDiscountedOrderTotal(
          {
            id: curr.id,
            parent_id: curr.parent_id,
            course_id: curr.course_id,
            registrations: curr.registrations
          },
          appliedDiscounts,
          promoDiscount
        );
        const discount = price > 0 ? 1 - discountedTotal / price : 0;

        const pricing: OrderPricing = {
          fullPrice: curr.registrations.reduce((acc, r) => acc + r.full_price, 0),
          price,
          discount,
          registrationLines: getRegistrationPricingLines(
            {
              id: curr.id,
              parent_id: curr.parent_id,
              course_id: curr.course_id,
              registrations: curr.registrations
            },
            appliedDiscounts,
            promoDiscount,
            titleCase
          )
        };
        let opc = { ...curr, cost: pricing };
        if (acc && acc.orders && acc.orders.length > 0) {
          acc.orders.push(opc);
        } else {
          acc = { location, orders: [opc] };
        }
      }
      return acc;
    }, {} as LocationOrders);

    setLocationOrders(locOrders);
  }, [values, appliedDiscounts, promoCode]);

  const initiatePayment = async () => {
    // Free order (or discounted below Stripe's 50-cent floor): no payment to
    // collect, so skip the card modal and complete the registration directly.
    if (total < 0.5) {
      const orderIds = locationOrders?.orders.map((o) => o.id) ?? [];
      const courseIds = locationOrders?.orders.map((o) => o.course.id).join(',') ?? '';
      if (orderIds.length === 0) return;
      await updateOrderTransaction({ ids: orderIds, transaction_id: 'FREE' }).unwrap();
      navigate(`/cart?complete=${locId}&course_ids=${courseIds}&order_ids=${orderIds.join(',')}`);
      return;
    }
    setShowModal(true);
  };

  const applySelectedScholarships = (selected: FormSelectOptions[]) => {
    const selectedDiscounts = values.discounts.filter((d) =>
      selected.some((option) => Number(option.value) === d.id)
    );
    setAppliedDiscounts(selectedDiscounts);

    if (selectedDiscounts.length === 0) {
      setScholarshipError(undefined);
      return;
    }

    const cartCourseIds = new Set(values.orders?.map((o) => o.course_id) ?? []);
    const anyMatch = selectedDiscounts.some((d) => cartCourseIds.has(d.course_id));

    if (!anyMatch) {
      const session = selectedDiscounts[0].course?.title ?? 'another session';
      setScholarshipError(`This scholarship is for ${session}, which isn't in your cart.`);
    } else {
      setScholarshipError(undefined);
    }
  };

  const searchForPromo = async (search: string) => {
    if (search) {
      try {
        let result = await searchPromoCode(search).unwrap();
        if (result && result.promotionalCode) {
          setPromoCode(result.promotionalCode);
          setPromoCodeMessage({ msg: 'Successfully found promo code', type: 'valid' });
        } else {
          setPromoCodeMessage({ msg: 'Invalid code', type: 'invalid' });
        }
      } catch (error) {
        setPromoCodeMessage({
          msg: getErrorMessage(error) || 'Failed to submit code, try again',
          type: 'invalid'
        });
      }
    }
  };

  let locId = values.location.id;
  let subtotal = locationOrders?.orders.reduce((acc, o) => acc + o.cost.fullPrice, 0) ?? 0;
  let total =
    locationOrders?.orders.reduce(
      (acc, o) =>
        acc +
        getDiscountedOrderTotal(
          {
            id: o.id,
            parent_id: o.parent_id,
            course_id: o.course_id,
            registrations: o.registrations
          },
          appliedDiscounts,
          (promoCode?.percent_discount ?? 0) / 100
        ),
      0
    ) ?? 0;
  let discount = total - subtotal;
  let discountLabel = '';
  return (
    <Row key={locId}>
      <h2>Orders - Sticky Fingers Cooking {values.location.title}</h2>
      <Col
        xs={12}
        md={8}
        className="pe-1"
      >
        <Stack gap={2}>
          {values.orders?.map((o, index) => (
            <Stack
              gap={1}
              key={`${locId}-${o.id}`}
            >
              <OrderCard
                key={`order-${locId}-${o.id}`}
                index={index}
                order={o}
              />
            </Stack>
          ))}
        </Stack>
      </Col>
      <Col
        xs={12}
        md={4}
        className="pe-1"
      >
        <Card className="order-summary-card">
          <Card.Body>
            <Stack gap={2}>
              <SFCLogoLarge
                style={{ width: '100%' }}
                className="order-summary-logo mb-3"
              />
              <h6 className="text-uppercase text-info mb-0">Order Summary</h6>
              {locationOrders?.orders?.map((o, index) => {
                return (
                  <Stack
                    gap={1}
                    key={index}
                  >
                    <OrderCost
                      key={`ordercost-${locId}-${index}`}
                      order={o}
                      index={index}
                    />
                  </Stack>
                );
              })}
              <LabelTextSpread label="Subtotal:">
                <CurrencyCell value={subtotal} />
              </LabelTextSpread>
              {discount !== 0 && (
                <LabelTextSpread label="Discounts:">
                  {discountLabel}
                  <CurrencyCell value={discount} />
                </LabelTextSpread>
              )}
              <LabelTextSpread
                fontSize={'1.25rem'}
                opacity={1}
                label="Total Due:"
              >
                <CurrencyCell value={total} />
              </LabelTextSpread>

              <Stack gap={2}>
                <SearchField
                  name="promoCodeSearch"
                  label="Promo Code"
                  onClick={() => searchForPromo(values.promoCodeSearch ?? '')}
                  clear={() => setPromoCode(undefined)}
                  message={promoCodeMessage}
                />
                <FormSelect
                  name={'scholarships'}
                  label="Scholarships"
                  multiple
                  options={
                    values.discounts?.map((d) => ({
                      label: formatDiscountOptionLabel(d, titleCase),
                      value: d.id
                    })) ?? []
                  }
                  onClick={applySelectedScholarships}
                  onSelectionChange={(selected) => {
                    if (selected.length === 0 || selected.length < appliedDiscounts.length) {
                      applySelectedScholarships(selected);
                    }
                  }}
                />
                {scholarshipError && (
                  <Form.Text className="text-danger">{scholarshipError}</Form.Text>
                )}
                <SimpleButton
                  type="button"
                  onClick={() => initiatePayment()}
                  disabled={showModal}
                  className="float-end mt-3"
                  variant="primary"
                >
                  Continue
                </SimpleButton>
              </Stack>
              <Modal
                show={showModal}
                onHide={() => {
                  setShowModal(false);
                }}
              >
                <Modal.Header closeButton>Payment Details</Modal.Header>
                <Card body>
                  <PaymentForm
                    location_id={locId}
                    locationOrders={locationOrders}
                    appliedDiscounts={appliedDiscounts}
                    promoCode={promoCode}
                    onRecovered={() => {
                      setShowModal(false);
                      window.scrollTo({ top: 0, behavior: 'auto' });
                    }}
                  />
                </Card>
              </Modal>
            </Stack>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};
